import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../integrations/supabase/client';
import { Check, Star, Home, Rocket, Crown, Building, Sparkles, ArrowRight, Loader2 } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { isLandlord } from '../utils/roleHelpers';

const AccountSetup = () => {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [checkingSubscription, setCheckingSubscription] = useState(true);
  
  useEffect(() => {
    const checkExistingSubscription = async () => {
      if (!user) return;
      
      try {
        const { data, error } = await supabase.functions.invoke('check-subscription');
        
        if (!error && data?.subscribed) {
          if (isLandlord(profile)) {
            navigate('/landlord-dashboard');
          } else {
            navigate('/dashboard');
          }
          return;
        }
      } catch (error) {
        console.error('Error checking subscription:', error);
      } finally {
        setCheckingSubscription(false);
      }
    };

    checkExistingSubscription();
  }, [user, profile, navigate]);

  const handleSubscribe = async (tier: string) => {
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please log in to continue with subscription.",
        variant: "destructive"
      });
      return;
    }
    
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { tier }
      });

      if (error) {
        toast({
          title: "Error",
          description: "Failed to create checkout session. Please try again.",
          variant: "destructive"
        });
      } else if (data?.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFreePlan = () => {
    if (isLandlord(profile)) {
      navigate('/landlord-dashboard');
    } else {
      navigate('/dashboard');
    }
  };

  if (!user || checkingSubscription) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <div className="text-center text-white">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
          <p>Setting up your account...</p>
        </div>
      </div>
    );
  }

  const isLandlordUser = isLandlord(profile);
  
  const renterPlans = [
    {
      name: 'Just Browsing',
      price: 'Free',
      period: '',
      description: 'Perfect for casual browsing and exploration',
      features: [
        '10 swipes per day',
        'Basic property browsing',
        'Limited AI matching with Fiona',
        'Basic messaging with landlords',
        'Property comparison (up to 3)',
        'Neighborhood insights'
      ],
      tier: 'free',
      popular: false
    },
    {
      name: 'All In',
      price: '$4.99',
      period: '/month',
      description: 'Unlimited access to all renter features',
      features: [
        'Unlimited daily swipes',
        'Advanced AI-powered matching',
        'Priority messaging with landlords',
        'Unlimited property comparisons',
        'Advanced neighborhood insights',
        'Priority customer support',
        'Early access to new features',
        'Saved searches & alerts'
      ],
      tier: 'all_in',
      popular: true
    }
  ];

  const landlordPlans = [
    {
      name: 'Starter',
      price: 'Free',
      period: 'to get started',
      unitRange: '1-3 units',
      pricePerUnit: '$12/unit/month',
      description: 'Perfect for getting started',
      features: [
        'List up to 3 properties',
        'AI-powered tenant matching',
        'Secure messaging with renters',
        'Tour scheduling',
        'Basic analytics',
        'Application management'
      ],
      tier: 'starter',
      popular: false
    },
    {
      name: 'Growth',
      price: '$9',
      period: '/unit/month',
      unitRange: '4-15 units',
      description: 'Scale your portfolio',
      features: [
        'Everything in Starter',
        'Advanced tenant screening',
        'Automated lease management',
        'Financial reporting',
        'Priority support',
        'Background checks'
      ],
      tier: 'growth',
      popular: true
    }
  ];

  const plansToShow = isLandlordUser ? landlordPlans : renterPlans;

  const getPlanIcon = (tier: string) => {
    switch (tier) {
      case 'starter': return <Home className="w-6 h-6 text-primary" />;
      case 'growth': return <Rocket className="w-6 h-6 text-primary" />;
      case 'pro': return <Crown className="w-6 h-6 text-primary" />;
      case 'enterprise': return <Building className="w-6 h-6 text-primary" />;
      case 'all_in': return <Sparkles className="w-6 h-6 text-primary" />;
      case 'free': return <Star className="w-6 h-6 text-primary" />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-20 w-72 h-72 bg-primary/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '3s' }}></div>
      </div>

      <div className="relative z-10 py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <div className="mb-6">
              <img 
                src="/lovable-uploads/d981c4c2-888d-4322-9d83-e527475f379f.png" 
                alt="Flamingo Logo" 
                className="h-12 w-auto mx-auto mb-6" 
              />
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
              {isLandlordUser ? "Start Managing Properties" : "Complete Your Setup"}
            </h1>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              {isLandlordUser 
                ? 'Get started for free. Upgrade as your portfolio grows.'
                : 'Select your plan to unlock the full Flamingo experience'
              }
            </p>
            {isLandlordUser && (
              <div className="mt-6 inline-flex items-center gap-2 bg-green-500/20 border border-green-500/30 text-green-400 px-4 py-2 rounded-full text-sm font-medium">
                <Sparkles className="w-4 h-4" />
                No credit card required to start
              </div>
            )}
          </div>

          <div className="grid gap-8 md:grid-cols-2 max-w-4xl mx-auto">
            {plansToShow.map((plan, index) => (
              <div 
                key={index} 
                className={`relative bg-slate-800/70 backdrop-blur-xl border border-slate-700/50 rounded-3xl p-8 transition-all duration-300 hover:scale-105 hover:bg-slate-800/80 ${
                  plan.popular ? 'ring-2 ring-primary shadow-2xl scale-105 shadow-primary/25' : 'hover:shadow-2xl'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-gradient-to-r from-primary to-blue-500 text-white px-6 py-2 rounded-full text-sm font-semibold shadow-lg flex items-center space-x-2">
                      <Crown className="w-4 h-4" />
                      <span>Recommended</span>
                    </div>
                  </div>
                )}

                <div className="text-center mb-8">
                  <h3 className="font-heading text-2xl font-bold text-white mb-2 flex items-center justify-center space-x-2">
                    {getPlanIcon(plan.tier)}
                    <span>{plan.name}</span>
                  </h3>
                  {'unitRange' in plan && (
                    <p className="text-primary font-medium text-sm mb-2">{(plan as any).unitRange}</p>
                  )}
                  <p className="text-slate-400 mb-6">{plan.description}</p>
                  
                  <div className="mb-2">
                    <span className="text-4xl font-bold text-white">{plan.price}</span>
                    {plan.period && <span className="text-slate-400 text-lg"> {plan.period}</span>}
                  </div>
                  {'pricePerUnit' in plan && (
                    <p className="text-slate-500 text-sm">Then {(plan as any).pricePerUnit}</p>
                  )}
                </div>

                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start space-x-3">
                      <Check className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-300">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button 
                  onClick={() => plan.tier === 'free' || plan.tier === 'starter' ? handleFreePlan() : handleSubscribe(plan.tier)}
                  disabled={loading}
                  className={`w-full py-4 text-lg font-semibold rounded-2xl transition-all duration-300 ${
                    plan.popular 
                      ? 'bg-gradient-to-r from-primary to-blue-500 hover:from-primary/90 hover:to-blue-500/90 text-white shadow-2xl hover:shadow-primary/25' 
                      : 'bg-slate-700/50 hover:bg-slate-600/50 text-white border border-slate-600/50 hover:border-slate-500/50'
                  }`}
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      {plan.tier === 'free' || plan.tier === 'starter' ? 'Get Started Free' : 'Subscribe Now'}
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <p className="text-slate-400 text-sm">
              {isLandlordUser 
                ? 'You can upgrade anytime as your portfolio grows'
                : 'You can always upgrade later from your dashboard'
              }
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountSetup;
